import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode, MapPin, Mail, Twitter, Facebook, Instagram, Heart, Shield, Server } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { API_BASE } from '../services/api';

export const Footer = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [serverIP, setServerIP] = useState('Loading...');

  useEffect(() => {
    const fetchServerIP = async () => {
      try {
        // Get the backend URL from dynamic detection (survives slot moves)
        const backendUrl = API_BASE || process.env.REACT_APP_BACKEND_URL || 'https://trackme.info';
        
        // First, always show which backend we're configured to use
        let deploymentInfo = '';
        if (backendUrl.includes('trackme.info')) {
          deploymentInfo = 'Production (trackme.info)';
        } else if (backendUrl.includes('preview')) {
          deploymentInfo = 'Preview Environment';
        } else {
          deploymentInfo = 'Development';
        }

        // Try to get additional server info if available
        try {
          const response = await fetch(`${backendUrl}/api/server-info`, { timeout: 3000 });
          if (response.ok) {
            const data = await response.json();
            setServerIP(`${deploymentInfo} | IP: ${data.server_ip || 'Unknown'}`);
          } else {
            setServerIP(deploymentInfo);
          }
        } catch {
          // If server-info fails, just show the deployment info
          setServerIP(deploymentInfo);
        }
      } catch (error) {
        setServerIP('Connection Error');
      }
    };

    fetchServerIP();
  }, []);

  return (
    <footer className={`${theme.footer.bg} ${theme.footer.text}`} style={{ fontFamily: theme.footer.fontFamily || 'inherit' }}>
      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-700 to-emerald-800 rounded-xl flex items-center justify-center">
                <QrCode className="w-6 h-6 text-amber-100" />
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold">iWasHere.me</span>
                <span className="text-xs text-gray-500">formerly TrackMe.info</span>
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              Celebrate your travels and create lasting memories. Track your adventures, 
              sign digital guestbooks, and share your journey with the world.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-emerald-600 transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-emerald-600 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-emerald-600 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {[
                "How It Works",
                "Featured Stories", 
                "Community",
                "Create QR Code",
                "Scan Item",
                "Help Center"
              ].map((link, index) => (
                <li key={index}>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    {link}
                  </a>
                </li>
              ))}

            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-bold mb-6">Resources</h3>
            <ul className="space-y-3">
              {[
                "Getting Started Guide",
                "QR Code Generator",
                "Mobile App",
                "API Documentation",
                "Safety Guidelines",
                "Terms of Service"
              ].map((link, index) => (
                <li key={index}>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Stats */}
          <div>
            <h3 className="text-lg font-bold mb-6">Get in Touch</h3>
            <div className="space-y-4 mb-8">
              <div>
                <p className="text-gray-300 text-sm mb-2">Questions or concerns? Email us here for help</p>
                <div className="flex items-center text-gray-400">
                  <Mail className="w-5 h-5 mr-3" />
                  <a 
                    href="mailto:Support@iWasHere.me" 
                    className="hover:text-emerald-400 transition-colors"
                  >
                    Support@iWasHere.me
                  </a>
                </div>
              </div>
              <div className="flex items-center text-gray-400">
                <MapPin className="w-5 h-5 mr-3" />
                <span>Worldwide Community</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-1 text-gray-400 mb-4 md:mb-0">
              <span>Made with</span>
              <Heart className="w-4 h-4 text-red-500 fill-current" />
              <span>for adventurers worldwide</span>
            </div>
            
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6 text-sm text-gray-400">
              <div className="flex flex-col md:flex-row items-center space-y-1 md:space-y-0 md:space-x-4">
                <span>© 2024 iWasHere.me. All rights reserved.</span>
                <div className="flex items-center space-x-2 text-xs bg-gray-800 px-3 py-1 rounded-full">
                  <Server className="w-3 h-3" />
                  <span>Server IP: {serverIP}</span>
                </div>
              </div>
              <div className="flex space-x-4">
                <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
                <a href="#" className="hover:text-white transition-colors">Cookie Policy</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};